import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  CircleDot,
  Clock3,
  Download,
  Eye,
  EyeOff,
  ExternalLink,
  Inbox,
  Lightbulb,
  Loader2,
  LogIn,
  LogOut,
  MessageSquareHeart,
  MessageCircle,
  Star,
  ShieldCheck,
  ThumbsDown,
  ThumbsUp,
  TriangleAlert,
  UserRoundCheck,
} from 'lucide-react'
import {
  AdminVoiceItem,
  clearSession,
  exchangeSso,
  getCurrentUser,
  getSsoLoginUrl,
  getStoredUser,
  getToken,
  loadAdmin,
  loadFeed,
  logout,
  markViewed,
  reauthenticate,
  refreshSession,
  SessionUser,
  updateAdmin,
  loadAdminComments,
  addAdminComment,
  ProcessingState,
  VoiceItem,
  VoiceStats,
  vote,
} from './api'
import { usePwaInstall } from './pwa'

type View = 'feed' | 'privacy' | 'admin'

const categories: Record<string, string> = {
  suggestion: 'Đề xuất',
  policy_process: 'Chính sách & quy trình',
  workplace_concern: 'Môi trường làm việc',
  compensation_benefits: 'Lương thưởng & quyền lợi',
  interpersonal_conflict: 'Quan hệ công việc',
  harassment_discrimination: 'Ứng xử không phù hợp',
  safety_security: 'An toàn & bảo mật',
  other: 'Khác',
}

const workflow: Record<ProcessingState, { step: number; label: string; className: string; description: string }> = {
  received: { step: 1, label: 'Đã ghi nhận', className: 'status-received', description: 'Góp ý đã vào hệ thống và chờ phân luồng.' },
  forwarded: { step: 2, label: 'Đã chuyển bộ phận liên quan', className: 'status-forwarded', description: 'Góp ý đã được chuyển đến bộ phận có trách nhiệm xử lý.' },
  coordinating: { step: 3, label: 'Đang phối hợp đúng đầu mối', className: 'status-coordinating', description: 'Bộ phận tiếp nhận đang phối hợp hoặc chuyển đến đầu mối phù hợp hơn.' },
  evaluating: { step: 4, label: 'Đang cân nhắc nguồn lực', className: 'status-evaluating', description: 'Giải pháp đang được đánh giá trong bối cảnh nguồn lực hiện tại.' },
  deferred: { step: 5, label: 'Sẽ xem xét vào thời điểm phù hợp', className: 'status-deferred', description: 'Ý tưởng được lưu để xem xét khi điều kiện phù hợp, chưa phải cam kết triển khai.' },
  resolved: { step: 6, label: 'Đã xử lý', className: 'status-resolved', description: 'Góp ý đã có kết quả xử lý và cập nhật công khai.' },
}

function processingStateOf(item: Pick<VoiceItem, 'processingState' | 'workflowStatus'>): ProcessingState {
  if (item.processingState) return item.processingState
  if (item.workflowStatus === 'resolved') return 'resolved'
  if (item.workflowStatus === 'in_progress') return 'forwarded'
  return 'received'
}

const LARK_APP_LINK = 'https://applink.larksuite.com/client/op/open'
const AUTH_FLOW_TIMEOUT_MS = 10_000

export function App() {
  const [user, setUser] = useState<SessionUser | null>(() => getStoredUser())
  const [checking, setChecking] = useState(true)
  const [authError, setAuthError] = useState('')
  const pwa = usePwaInstall()

  useEffect(() => {
    let active = true
    let timedOut = false
    const isSsoCallback = window.location.pathname === '/auth/sso/callback'
    const hadLocalSession = Boolean(getToken())
    const controller = new AbortController()
    const deadline = window.setTimeout(() => {
      timedOut = true
      controller.abort()
      if (!active) return
      if (isSsoCallback || hadLocalSession) {
        reauthenticate()
        return
      }
      clearSession()
      setUser(null)
      setAuthError('SSO phản hồi quá lâu. Vui lòng thử đăng nhập lại.')
      setChecking(false)
    }, AUTH_FLOW_TIMEOUT_MS)

    async function bootstrap() {
      try {
        if (isSsoCallback) {
          const params = new URLSearchParams(window.location.search)
          const code = params.get('code') || params.get('ssoToken')
          // One-time SSO codes must never survive a failed exchange or reload.
          window.history.replaceState({}, '', '/')
          if (!code) {
            reauthenticate()
            return
          }
          const nextUser = await exchangeSso(code, controller.signal)
          if (active && !timedOut) setUser(nextUser)
          return
        }

        if (getToken()) {
          try {
            const nextUser = await getCurrentUser(controller.signal)
            if (active && !timedOut) setUser(nextUser)
            return
          } catch (error) {
            if (controller.signal.aborted) throw error
            // Access token may have expired; the refresh cookie can still recover it.
          }
        }

        const nextUser = await refreshSession(controller.signal)
        if (active && !timedOut) {
          if (hadLocalSession && !nextUser) {
            reauthenticate()
            return
          }
          setUser(nextUser)
        }
      } catch (error) {
        if (active && !timedOut) {
          if (isSsoCallback || hadLocalSession) {
            reauthenticate()
            return
          }
          clearSession()
          setUser(null)
          setAuthError(controller.signal.aborted
            ? 'SSO phản hồi quá lâu. Vui lòng thử đăng nhập lại.'
            : messageOf(error))
        }
      } finally {
        window.clearTimeout(deadline)
        if (active && !timedOut) setChecking(false)
      }
    }

    // Defer one tick so React StrictMode can clean up its development-only probe
    // before a one-time SSO code is exchanged.
    const start = window.setTimeout(() => void bootstrap(), 0)
    return () => {
      active = false
      window.clearTimeout(start)
      window.clearTimeout(deadline)
      controller.abort()
    }
  }, [])

  if (checking) return <CenteredState icon={<Loader2 className="spin" />} title="Đang kiểm tra phiên SSO" text="Vui lòng đợi trong giây lát." />
  if (!user) return <Login error={authError} pwa={pwa} />
  return <Dashboard user={user} pwa={pwa} onExpired={reauthenticate} />
}

type PwaInstall = ReturnType<typeof usePwaInstall>

function Login({ error, pwa }: { error: string; pwa: PwaInstall }) {
  return (
    <main className="login-shell" id="main-content">
      <section className="login-card" aria-labelledby="login-title">
        <div className="brand-mark"><MessageSquareHeart aria-hidden="true" /></div>
        <p className="eyebrow">Kênh nội bộ GearVN</p>
        <h1 id="login-title">Có điều khó nói, vẫn có nơi để nói.</h1>
        <p className="lead">Gửi qua agent Lark. Dashboard chỉ hiển thị bản đã khử nhận diện và dùng SSO để bảo vệ quyền xem, chống vote trùng.</p>
        <div className="trust-list">
          <span><EyeOff aria-hidden="true" /> Không hiện tên người gửi</span>
          <span><ShieldCheck aria-hidden="true" /> Nội dung nhạy cảm phải duyệt</span>
          <span><UserRoundCheck aria-hidden="true" /> Một lựa chọn mỗi tài khoản</span>
        </div>
        {error && <div className="alert" role="alert"><TriangleAlert aria-hidden="true" />{error}</div>}
        <a className="button primary full" href={getSsoLoginUrl()}>
          <LogIn aria-hidden="true" /> Đăng nhập SSO GearVN <ArrowRight aria-hidden="true" />
        </a>
        {!pwa.installed && (pwa.canInstall || pwa.isIos) && <button className="button secondary full" type="button" onClick={() => void pwa.install()}><Download />{pwa.isIos && !pwa.canInstall ? 'Cài bằng Chia sẻ → Thêm vào Màn hình chính' : 'Cài dashboard thành ứng dụng'}</button>}
        <p className="microcopy">SSO không được hiển thị cùng nội dung đề xuất. Hệ thống vẫn có log bảo mật phục vụ vận hành.</p>
      </section>
    </main>
  )
}

function Dashboard({ user, pwa, onExpired }: { user: SessionUser; pwa: PwaInstall; onExpired: () => void }) {
  const [view, setView] = useState<View>(() => user.isAdmin ? 'admin' : 'feed')
  const [items, setItems] = useState<VoiceItem[]>([])
  const [stats, setStats] = useState<VoiceStats>({ published: 0, pending: 0, inProgress: 0, resolved: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [department, setDepartment] = useState('all')

  async function reload(silent = false) {
    if (!silent) setLoading(true)
    setError('')
    try {
      const data = await loadFeed()
      setItems(Array.isArray(data.items) ? data.items : [])
      setStats(data.stats ?? { published: 0, pending: 0, inProgress: 0, resolved: 0 })
    } catch (err) {
      const message = messageOf(err)
      if (message.includes('hết hạn')) onExpired()
      else setError(message)
    } finally {
      if (!silent) setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
    const refreshVisibleFeed = () => {
      if (document.visibilityState === 'visible') void reload(true)
    }
    const interval = window.setInterval(refreshVisibleFeed, 30_000)
    window.addEventListener('focus', refreshVisibleFeed)
    document.addEventListener('visibilitychange', refreshVisibleFeed)
    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', refreshVisibleFeed)
      document.removeEventListener('visibilitychange', refreshVisibleFeed)
    }
  }, [])

  const departments = useMemo(() => [...new Set(items.map((item) => item.departmentCode).filter(Boolean) as string[])].sort(), [items])
  const visibleItems = department === 'all' ? items : items.filter((item) => item.departmentCode === department)

  return (
    <div className="app-shell">
      <header className="topbar">
        <button className="brand-button" onClick={() => setView('feed')} aria-label="Về bảng đề xuất">
          <span className="brand-mark small"><MessageSquareHeart aria-hidden="true" /></span>
          <span><strong>Lắng nghe nội bộ</strong><small>Góp ý an toàn · Hành động minh bạch</small></span>
        </button>
        <div className="user-area">
          {!pwa.installed && pwa.canInstall && <button className="install-button" onClick={() => void pwa.install()}><Download />Cài ứng dụng</button>}
          {user.isAdmin && <span className="admin-badge"><ShieldCheck aria-hidden="true" />Quản trị</span>}
          <span className="user-name">{user.name}</span>
          <button className="icon-button" onClick={() => void logout()} aria-label="Đăng xuất"><LogOut aria-hidden="true" /></button>
        </div>
      </header>

      <div className="layout">
        <aside className="sidebar" aria-label="Điều hướng chính">
          <NavButton active={view === 'feed'} icon={<Lightbulb />} label="Bảng đề xuất" onClick={() => setView('feed')} />
          <NavButton active={view === 'privacy'} icon={<ShieldCheck />} label="Quyền riêng tư" onClick={() => setView('privacy')} />
          {user.isAdmin && <NavButton active={view === 'admin'} icon={<Inbox />} label="Hàng đợi duyệt" onClick={() => setView('admin')} badge={stats.pending} />}
          <div className="sidebar-note">
            <EyeOff aria-hidden="true" />
            <p><strong>Không hiện tên người gửi</strong><br />Bản public đã được khử nhận diện.</p>
          </div>
        </aside>

        <main id="main-content" className="content" tabIndex={-1}>
          {view === 'feed' && (
            <FeedView
              items={visibleItems}
              stats={stats}
              loading={loading}
              error={error}
              departments={departments}
              department={department}
              setDepartment={setDepartment}
              onReload={reload}
              onVote={async (id, value) => {
                const result = await vote(id, value)
                setItems((current) => current.map((item) => item.publicId === id ? { ...item, viewerVote: result.vote, likeCount: result.likeCount, dislikeCount: result.dislikeCount, ratingPoints: result.likeCount - result.dislikeCount } : item))
              }}
              onViewed={async (id) => {
                const result = await markViewed(id)
                setItems((current) => current.map((item) => item.publicId === id ? { ...item, viewCount: result.viewCount } : item))
              }}
            />
          )}
          {view === 'privacy' && <PrivacyView />}
          {view === 'admin' && user.isAdmin && <AdminView onStatsChanged={reload} />}
        </main>
      </div>

      <nav className="bottom-nav" aria-label="Điều hướng trên điện thoại">
        <NavButton active={view === 'feed'} icon={<Lightbulb />} label="Đề xuất" onClick={() => setView('feed')} />
        <NavButton active={view === 'privacy'} icon={<ShieldCheck />} label="Riêng tư" onClick={() => setView('privacy')} />
        {user.isAdmin && <NavButton active={view === 'admin'} icon={<Inbox />} label="Duyệt" onClick={() => setView('admin')} badge={stats.pending} />}
      </nav>
    </div>
  )
}

function FeedView(props: {
  items: VoiceItem[]
  stats: VoiceStats
  loading: boolean
  error: string
  departments: string[]
  department: string
  setDepartment: (value: string) => void
  onReload: () => Promise<void>
  onVote: (id: string, value: 1 | -1) => Promise<void>
  onViewed: (id: string) => Promise<void>
}) {
  const [statusFilter, setStatusFilter] = useState<'all' | 'received' | 'in_progress' | 'resolved'>('all')
  const { allItems, groups } = useMemo(() => {
    const byDate = (a: VoiceItem, b: VoiceItem) => new Date(b.createdAt || b.publishedAt).getTime() - new Date(a.createdAt || a.publishedAt).getTime()
    const byRating = (a: VoiceItem, b: VoiceItem) => (b.ratingPoints ?? b.likeCount - b.dislikeCount) - (a.ratingPoints ?? a.likeCount - a.dislikeCount) || byDate(a, b)
    return {
      allItems: [...props.items].sort(byRating),
      groups: [
        { key: 'received', title: 'Ticket mới', description: 'Ưu tiên ticket mới nhất theo ngày tạo.', items: props.items.filter((item) => item.workflowStatus === 'received').sort(byDate) },
        { key: 'in_progress', title: 'Đang xử lý', description: 'Các ticket đang được chuyển, phối hợp hoặc cân nhắc, xếp theo point cao nhất.', items: props.items.filter((item) => item.workflowStatus === 'in_progress').sort(byRating) },
        { key: 'resolved', title: 'Đã xử lý', description: 'Kết quả đã hoàn tất, xếp theo point cao nhất.', items: props.items.filter((item) => item.workflowStatus === 'resolved').sort(byRating) },
      ],
    }
  }, [props.items])
  const filterOptions = [
    { key: 'all' as const, label: 'Tất cả', count: props.items.length },
    { key: 'received' as const, label: 'Mới', count: groups[0].items.length },
    { key: 'in_progress' as const, label: 'Đang xử lý', count: groups[1].items.length },
    { key: 'resolved' as const, label: 'Đã xử lý', count: groups[2].items.length },
  ]
  const visibleGroups = groups.filter((group) => group.key === statusFilter)

  return (
    <>
      <section className="voice-cta" aria-labelledby="voice-cta-title">
        <div className="voice-cta-copy">
          <span className="voice-cta-icon"><MessageSquareHeart aria-hidden="true" /></span>
          <div>
            <strong id="voice-cta-title">Gửi qua G TEL</strong>
            <span>Mỗi tin nhắn một vấn đề · mở ứng dụng Lark rồi chọn G TEL.</span>
          </div>
        </div>
        <a className="voice-cta-action" href={LARK_APP_LINK} aria-label="Mở ứng dụng Lark để gửi qua G TEL">
          Mở Lark <ExternalLink aria-hidden="true" />
        </a>
      </section>

      <section className="stats-grid" aria-label="Tổng quan tiến độ">
        <Stat icon={<Lightbulb />} value={props.stats.published} label="Đề xuất công khai" />
        <Stat icon={<Clock3 />} value={props.stats.inProgress} label="Đang xử lý" />
        <Stat icon={<CheckCircle2 />} value={props.stats.resolved} label="Đã xử lý" />
      </section>

      <section className="status-filter" aria-label="Lọc nhanh theo trạng thái">
        {filterOptions.map((option) => (
          <button
            className={`filter-tab${statusFilter === option.key ? ' active' : ''}`}
            type="button"
            key={option.key}
            aria-pressed={statusFilter === option.key}
            aria-label={`${option.label}: ${option.count} ticket`}
            onClick={() => setStatusFilter(option.key)}
          >
            <span>{option.label}</span><strong>{option.count}</strong>
          </button>
        ))}
      </section>

      <section className="feed-toolbar">
        <div><h2>Bảng đề xuất chung</h2><p>Mỗi tài khoản có một lựa chọn đang hiệu lực cho mỗi đề xuất.</p></div>
        <label className="select-label">Bộ phận
          <select value={props.department} onChange={(event) => props.setDepartment(event.target.value)}>
            <option value="all">Tất cả bộ phận</option>
            {props.departments.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>
      </section>

      {props.error && <div className="alert" role="alert"><TriangleAlert />{props.error}<button onClick={() => void props.onReload()}>Thử lại</button></div>}
      {props.loading ? <div className="loading-list"><Loader2 className="spin" /> Đang tải đề xuất…</div> : (
        <div className="status-board">
          {statusFilter === 'all' ? (
            <div className="voice-list" aria-label="Tất cả ticket theo xếp hạng">
              {allItems.map((item) => <VoiceCard key={item.publicId} item={item} onVote={props.onVote} onViewed={props.onViewed} />)}
            </div>
          ) : visibleGroups.map((group) => <section className="ticket-block" key={group.key} aria-labelledby={`ticket-${group.key}`}>
              <div className="ticket-block-heading"><div><h2 id={`ticket-${group.key}`}>{group.title}</h2><p>{group.description}</p></div><strong>{group.items.length}</strong></div>
              <div className="voice-list">{group.items.map((item) => <VoiceCard key={item.publicId} item={item} onVote={props.onVote} onViewed={props.onViewed} />)}{!group.items.length && <p className="empty-block">Chưa có ticket ở trạng thái này.</p>}</div>
            </section>)}
          {!props.items.length && <EmptyState />}
        </div>
      )}
    </>
  )
}

function VoiceCard({ item, onVote, onViewed }: { item: VoiceItem; onVote: (id: string, value: 1 | -1) => Promise<void>; onViewed: (id: string) => Promise<void> }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const cardRef = useRef<HTMLElement | null>(null)
  const viewRecorded = useRef(false)
  const status = workflow[processingStateOf(item)]
  useEffect(() => {
    const card = cardRef.current
    if (!card || viewRecorded.current) return
    let timer: number | undefined
    const record = () => {
      if (viewRecorded.current) return
      viewRecorded.current = true
      void onViewed(item.publicId).catch(() => undefined)
    }
    if (typeof IntersectionObserver === 'undefined') {
      timer = globalThis.setTimeout(record, 600)
      return () => globalThis.clearTimeout(timer)
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting || entry.intersectionRatio < 0.5) {
        if (timer) globalThis.clearTimeout(timer)
        timer = undefined
        return
      }
      if (!timer) timer = globalThis.setTimeout(() => { observer.disconnect(); record() }, 600)
    }, { threshold: [0.5] })
    observer.observe(card)
    return () => {
      observer.disconnect()
      if (timer) globalThis.clearTimeout(timer)
    }
  }, [item.publicId])
  async function choose(value: 1 | -1) {
    if (busy || item.viewerVote === value) return
    setBusy(true); setError('')
    try { await onVote(item.publicId, value) }
    catch (err) { setError(messageOf(err)) }
    finally { setBusy(false) }
  }
  return (
    <article className="voice-card" ref={cardRef}>
      <div className="card-topline">
        <span className="category-pill">{categories[item.category] ?? 'Góp ý'}</span>
        <span className={`status-pill ${status.className}`}><CircleDot aria-hidden="true" />{status.step}/6 · {status.label}</span>
      </div>
      <h3>{item.title}</h3>
      <p>{item.body}</p>
      <div className="card-meta">
        <span>{item.departmentCode || 'Liên bộ phận'}</span>
        <span aria-label={`Mã đề xuất ${item.publicId}`}>{item.publicId}</span>
        <time dateTime={item.createdAt || item.publishedAt}>Tạo ngày {formatDate(item.createdAt || item.publishedAt)}</time>
        {item.statusUpdatedAt && <time dateTime={item.statusUpdatedAt}>Cập nhật trạng thái {formatDateTime(item.statusUpdatedAt)}</time>}
      </div>
      <div className="engagement-row" aria-label="Mức độ quan tâm">
        <div className="rating-point" aria-label={`${item.ratingPoints ?? item.likeCount - item.dislikeCount} point`}><Star aria-hidden="true" /><strong>{item.ratingPoints ?? item.likeCount - item.dislikeCount}</strong> point</div>
        {item.viewCount !== undefined && <div className="view-count" aria-label={`${item.viewCount} người đã xem`} title="Số tài khoản SSO duy nhất đã nhìn thấy ticket"><Eye aria-hidden="true" /><strong>{item.viewCount}</strong> đã xem</div>}
      </div>
      <div className="vote-row" aria-label="Đánh giá đề xuất">
        <button className={item.viewerVote === 1 ? 'vote active-like' : 'vote'} onClick={() => void choose(1)} disabled={busy} aria-pressed={item.viewerVote === 1}>
          <ThumbsUp aria-hidden="true" /> Hữu ích <strong>{item.likeCount}</strong>
        </button>
        <button className={item.viewerVote === -1 ? 'vote active-dislike' : 'vote'} onClick={() => void choose(-1)} disabled={busy} aria-pressed={item.viewerVote === -1}>
          <ThumbsDown aria-hidden="true" /> Chưa đồng ý <strong>{item.dislikeCount}</strong>
        </button>
        {busy && <Loader2 className="spin inline-loader" aria-label="Đang lưu lựa chọn" />}
      </div>
      {!!item.adminComments?.length && <section className="public-updates" aria-label="Cập nhật xử lý từ quản trị">
        <h4><MessageCircle aria-hidden="true" />Cập nhật xử lý</h4>
        {item.adminComments.map((comment) => <article key={comment.id}><p>{comment.body}</p><small>Admin · <time dateTime={comment.createdAt}>{formatDateTime(comment.createdAt)}</time></small></article>)}
      </section>}
      {error && <p className="field-error" role="alert">{error}</p>}
    </article>
  )
}

function PrivacyView() {
  return (
    <section className="reading-page">
      <p className="eyebrow">Quyền riêng tư</p>
      <h1>Ẩn tên trên dashboard, minh bạch về giới hạn.</h1>
      <p className="lead">Hệ thống được thiết kế để giảm rủi ro lộ người gửi, không đưa ra lời hứa “ẩn danh tuyệt đối”.</p>
      <div className="principle-grid">
        <Principle icon={<EyeOff />} title="Không hiện tên" text="Feed chung không có tên, email, ID Lark hay thông tin liên hệ của người gửi." />
        <Principle icon={<ShieldCheck />} title="Duyệt trước khi công khai" text="Lương thưởng, chỉ trích cá nhân, công kích, cáo buộc và vấn đề an toàn luôn vào hàng đợi riêng." />
        <Principle icon={<UserRoundCheck />} title="SSO chống trùng" text="Danh tính SSO được băm tại lớp máy chủ để chống vote và lượt xem trùng; feed không nhận tên hoặc email." />
      </div>
      <div className="notice-panel">
        <TriangleAlert aria-hidden="true" />
        <div><strong>Điều cần biết trước khi gửi</strong><p>GoClaw/Lark vẫn có log vận hành bảo mật. Đừng gửi mật khẩu, token, dữ liệu khách hàng hoặc bí mật không cần thiết. Nếu có nguy hiểm tức thời, hãy dùng kênh khẩn cấp phù hợp thay vì chỉ chờ dashboard.</p></div>
      </div>
      <h2>Luồng xử lý</h2>
      <ol className="steps">
        <li><span>1</span><div><strong>Nhắn agent Lark</strong><p>Chia sẻ theo cách bạn thấy thoải mái; bộ phận là tùy chọn.</p></div></li>
        <li><span>2</span><div><strong>Phân loại & khử nhận diện</strong><p>Agent tạo bản public-safe; server kiểm tra nhạy cảm thêm một lớp.</p></div></li>
        <li><span>3</span><div><strong>Duyệt hoặc công khai</strong><p>Nội dung an toàn có thể lên feed; nội dung nhạy cảm chờ quản trị.</p></div></li>
        <li><span>4</span><div><strong>Cập nhật tiến độ</strong><p>Quản trị chọn một trong sáu mốc, từ Đã ghi nhận đến Đã xử lý.</p></div></li>
      </ol>
    </section>
  )
}

function AdminView({ onStatsChanged }: { onStatsChanged: () => Promise<void> }) {
  const [items, setItems] = useState<AdminVoiceItem[]>([])
  const [selected, setSelected] = useState<AdminVoiceItem | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  async function reload() {
    setLoading(true); setError('')
    try {
      const data = await loadAdmin()
      setItems(Array.isArray(data.items) ? data.items : [])
      if (selected) setSelected(data.items.find((item) => item.publicId === selected.publicId) ?? null)
    } catch (err) { setError(messageOf(err)) }
    finally { setLoading(false) }
  }
  useEffect(() => { void reload() }, [])
  return (
    <section>
      <div className="admin-heading"><div><p className="eyebrow">Chỉ quản trị</p><h1>Hàng đợi kiểm duyệt</h1><p>Ưu tiên rủi ro cao. Không copy nội dung gốc sang kênh công khai.</p></div><span className="confidential-badge"><ShieldCheck />Nội dung mật</span></div>
      {error && <div className="alert" role="alert"><TriangleAlert />{error}</div>}
      {loading ? <div className="loading-list"><Loader2 className="spin" /> Đang tải hàng đợi…</div> : (
        <div className="admin-grid">
          <div className="queue-list">
            {items.map((item) => (
              <button key={item.publicId} className={selected?.publicId === item.publicId ? 'queue-item selected' : 'queue-item'} onClick={() => setSelected(item)}>
                <span className={`risk-dot risk-${item.riskLevel}`} aria-label={`Rủi ro ${item.riskLevel}`} />
                <span><strong>{item.title || 'Chưa có bản public'}</strong><small>{item.publicId} · {categories[item.category]}</small></span>
                <ChevronRight aria-hidden="true" />
              </button>
            ))}
            {!items.length && <EmptyState />}
          </div>
          <div className="review-panel">
            {selected ? <ReviewForm item={selected} onSaved={async () => { await reload(); await onStatsChanged() }} /> : <div className="review-placeholder"><Inbox /><h2>Chọn một nội dung để duyệt</h2><p>Nội dung gốc chỉ hiện ở khu vực quản trị này.</p></div>}
          </div>
        </div>
      )}
    </section>
  )
}

function ReviewForm({ item, onSaved }: { item: AdminVoiceItem; onSaved: () => Promise<void> }) {
  const [title, setTitle] = useState(item.title)
  const [body, setBody] = useState(item.body)
  const [note, setNote] = useState(item.adminNote)
  const [status, setStatus] = useState<ProcessingState>(() => processingStateOf(item))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [comments, setComments] = useState(item.adminComments ?? [])
  const [commentBody, setCommentBody] = useState('')
  const [commentBusy, setCommentBusy] = useState(false)
  useEffect(() => {
    setTitle(item.title); setBody(item.body); setNote(item.adminNote); setStatus(processingStateOf(item)); setError(''); setSuccess(''); setComments(item.adminComments ?? [])
    void loadAdminComments(item.publicId).then((data) => setComments(data.items ?? [])).catch((err) => setError(messageOf(err)))
  }, [item.publicId])
  async function save(action = '') {
    const statusChangedAt = new Date(item.statusUpdatedAt || item.createdAt).getTime()
    const hasCurrentPublicUpdate = comments.some((entry) => new Date(entry.createdAt).getTime() >= statusChangedAt)
    if (status === 'resolved' && !hasCurrentPublicUpdate) {
      setError('Hãy đăng một cập nhật công khai sau lần đổi trạng thái gần nhất trước khi đánh dấu Đã xử lý.')
      return
    }
    setBusy(true); setError(''); setSuccess('')
    try {
      await updateAdmin(item.publicId, { action, publicTitle: title, publicBody: body, adminNote: note, processingState: status })
      setSuccess(action === 'approve' ? 'Đã duyệt và công khai bản ẩn danh.' : 'Đã cập nhật.')
      await onSaved()
    } catch (err) { setError(messageOf(err)) }
    finally { setBusy(false) }
  }
  async function comment() {
    if (!commentBody.trim()) return
    setCommentBusy(true); setError(''); setSuccess('')
    try {
      const created = await addAdminComment(item.publicId, commentBody)
      setComments((current) => [...current, created]); setCommentBody(''); setSuccess('Đã đăng cập nhật xử lý lên ticket.')
    } catch (err) { setError(messageOf(err)) }
    finally { setCommentBusy(false) }
  }
  return (
    <form onSubmit={(event) => { event.preventDefault(); void save() }}>
      <div className="review-title"><div><span className={`risk-label risk-${item.riskLevel}`}>Rủi ro {item.riskLevel}</span><h2>{item.publicId}</h2></div><span>{item.moderationState}</span></div>
      <div className="sensitive-tags">{item.sensitivityLabels.map((label) => <span key={label}>{label}</span>)}</div>
      <label>Nội dung gốc — không công khai<textarea className="raw-text" value={item.rawText} readOnly rows={7} /></label>
      <label>Tiêu đề public<input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={180} /></label>
      <label>Bản public đã khử nhận diện<textarea value={body} onChange={(event) => setBody(event.target.value)} rows={5} maxLength={6000} /></label>
      <div className="form-row">
        <label>Trạng thái<select value={status} onChange={(event) => setStatus(event.target.value as ProcessingState)}>
          <option value="received">1. Đã ghi nhận</option>
          <option value="forwarded">2. Đã chuyển bộ phận liên quan</option>
          <option value="coordinating">3. Đang phối hợp đúng đầu mối</option>
          <option value="evaluating">4. Đang cân nhắc nguồn lực</option>
          <option value="deferred">5. Sẽ xem xét vào thời điểm phù hợp</option>
          <option value="resolved">6. Đã xử lý</option>
        </select><span className="field-help">{workflow[status]?.description}</span></label>
        <label>Ghi chú quản trị<input value={note} onChange={(event) => setNote(event.target.value)} maxLength={4000} /></label>
      </div>
      <section className="admin-comments">
        <h3><MessageCircle />Cập nhật xử lý công khai</h3>
        <p className="field-help">Nhân viên sẽ thấy nguyên văn nội dung này cùng thời gian đăng. Không nhập dữ liệu nhận diện hoặc nội dung mật.</p>
        <div className="comment-thread">{comments.map((entry) => <article key={entry.id}><p>{entry.body}</p><small>{entry.authorId} · {formatDateTime(entry.createdAt)}</small></article>)}{!comments.length && <p className="empty-block">Chưa có cập nhật xử lý.</p>}</div>
        <label>Comment của Admin<textarea value={commentBody} onChange={(event) => setCommentBody(event.target.value)} rows={3} maxLength={2000} placeholder="Ví dụ: Đã ghi nhận và đang làm việc với bộ phận liên quan." /></label>
        <button type="button" className="button secondary" disabled={commentBusy || !commentBody.trim()} onClick={() => void comment()}>{commentBusy ? <Loader2 className="spin" /> : <MessageCircle />}Đăng cập nhật</button>
      </section>
      {error && <p className="field-error" role="alert">{error}</p>}
      {success && <p className="field-success" role="status">{success}</p>}
      <div className="action-bar">
        <button type="button" className="button primary" disabled={busy} onClick={() => void save('approve')}><CheckCircle2 />Duyệt & công khai</button>
        <button type="button" className="button secondary" disabled={busy} onClick={() => void save('redaction_required')}>Yêu cầu khử nhận diện lại</button>
        <button type="button" className="button ghost" disabled={busy} onClick={() => void save('private_case')}>Giữ hồ sơ riêng</button>
        <button type="submit" className="button ghost" disabled={busy}>Lưu trạng thái</button>
      </div>
    </form>
  )
}

function NavButton({ active, icon, label, onClick, badge }: { active: boolean; icon: React.ReactNode; label: string; onClick: () => void; badge?: number }) {
  return <button className={active ? 'nav-button active' : 'nav-button'} onClick={onClick} aria-current={active ? 'page' : undefined}>{icon}<span>{label}</span>{badge ? <b>{badge}</b> : null}</button>
}

function Stat({ icon, value, label }: { icon: React.ReactNode; value: number; label: string }) {
  return <div className="stat-card"><span>{icon}</span><div><strong>{value}</strong><small>{label}</small></div></div>
}

function Principle({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return <article className="principle"><span>{icon}</span><h2>{title}</h2><p>{text}</p></article>
}

function EmptyState() {
  return <div className="empty-state"><Lightbulb /><h3>Chưa có nội dung phù hợp</h3><p>Đề xuất sẽ xuất hiện sau khi được khử nhận diện và duyệt an toàn.</p></div>
}

function CenteredState({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return <main className="centered-state" id="main-content">{icon}<h1>{title}</h1><p>{text}</p></main>
}

function formatDate(value: string): string {
  try { return new Intl.DateTimeFormat('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(value)) }
  catch { return value }
}

function formatDateTime(value: string): string {
  try { return new Intl.DateTimeFormat('vi-VN', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value)) }
  catch { return value }
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : 'Đã có lỗi. Vui lòng thử lại.'
}
